import {translate} from './translations.js';
import {validateForm, getValidationContainer} from '../valid/form.js';

const FORM = '.form_for_js_in_div';
const PARTS = 'input[name="totp_code_parts[]"]';
const INPUT = '[data-totp-backup-input]';
const CELL = '[data-totp-backup-part]';
const TOGGLE = '[data-totp-backup-toggle]';
const SUBMIT = '.button_for_js_in_div.necessarily-impacted';

const pending = form => form.querySelector(SUBMIT)?.dataset.requestPending === '1';

const cellsOf = form => [...form.querySelectorAll(CELL)];
const focus = cell => {cell.focus(); cell.select();};
const sync = form => {
    const scalar = form?.querySelector(INPUT);
    if (!scalar) return;
    const cells = cellsOf(form);
    const code = cells.map(cell => cell.value).join('');
    scalar.value = cells.length === 8 && cells.every(cell => /^[A-F0-9]$/.test(cell.value))
        ? `${code.slice(0, 4)}-${code.slice(4)}` : '';
    validateForm(getValidationContainer(scalar));
};

// Reject malformed clipboard text and overlong codes instead of truncating.
const distribute = (form, start, text) => {
    const raw = String(text).trim().toUpperCase();
    const code = /^[A-F0-9]{4}-[A-F0-9]{4}$/.test(raw) ? raw.replace('-', '') : raw;
    const cells = cellsOf(form);
    if (!/^[A-F0-9]+$/.test(code) || code.length > 8) return false;
    const offset = code.length === 8 ? 0 : start;
    if (code.length > cells.length - offset) return false;
    [...code].forEach((character, index) => {cells[offset + index].value = character;});
    sync(form);
    focus(cells[Math.min(offset + code.length, 7)]);
    return true;
};

const enhance = form => {
    // Enrollment confirmation is rendered by PHP with eight existing cells.
    // Supply the scalar field consumed by validation and js_form serialization.
    if (form.querySelector(SUBMIT)?.name === 'totp_backup_code_check') {
        if (form.querySelector(INPUT) || cellsOf(form).length !== 8) return;
        const input = document.createElement('input');
        input.type = 'text';
        input.hidden = true;
        input.name = 'totp_code';
        input.dataset.totpBackupInput = '1';
        input.pattern = '[A-F0-9]{4}-[A-F0-9]{4}';
        input.required = true;
        input.className = 'input-necessarily';
        cellsOf(form)[0].parentElement.append(input);
        sync(form);
        return;
    }
    if (form.querySelector(TOGGLE)) return;
    const submit = form.querySelector(SUBMIT);
    if (!['totp_check_auth', 'totp_status_disable'].includes(submit?.name)) return;
    const group = form.querySelector('.otp-field-wrapper');
    const fields = [...form.querySelectorAll(PARTS)];
    if (!group || fields.length !== 6) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'totp-backup';
    wrapper.hidden = true;
    wrapper.setAttribute('role', 'group');
    wrapper.setAttribute('aria-label', translate('Backup code'));
    const title = document.createElement('div');
    title.className = 'totp-backup-label';
    title.textContent = translate('Backup code');
    const row = document.createElement('div');
    row.className = 'totp-backup-fields';
    const cells = Array.from({length: 8}, (_, index) => {
        if (index === 4) {
            const separator = document.createElement('span');
            separator.className = 'totp-backup-separator';
            separator.textContent = '-';
            separator.setAttribute('aria-hidden', 'true');
            row.append(separator);
        }
        const cell = document.createElement('input');
        cell.type = 'text';
        cell.dataset.totpBackupPart = String(index);
        cell.maxLength = 1;
        cell.pattern = '[A-Fa-f0-9]';
        cell.inputMode = 'text';
        cell.autocomplete = 'off';
        cell.setAttribute('autocapitalize', 'characters');
        cell.setAttribute('aria-label', translate('Backup code character {position} of 8', {position: index + 1}));
        cell.spellcheck = false;
        cell.disabled = true;
        row.append(cell);
        return cell;
    });
    const input = document.createElement('input');
    input.type = 'text';
    input.hidden = true;
    input.name = 'totp_code';
    input.dataset.totpBackupInput = '1';
    input.pattern = '[A-F0-9]{4}-[A-F0-9]{4}';
    input.disabled = true;
    wrapper.append(title, row, input);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.dataset.totpBackupToggle = '1';
    toggle.className = 'form-auth-switch__item btn--like-link';
    const toggleIcon = document.createElement('img');
    toggleIcon.src = '/template/images/svg/auth/lost_password.svg';
    toggleIcon.className = 'icon to-svg';
    toggleIcon.alt = '';
    toggleIcon.setAttribute('aria-hidden', 'true');
    const toggleLabel = document.createElement('span');
    toggleLabel.textContent = translate('Use a backup code');
    toggle.append(toggleIcon, toggleLabel);
    toggle.setAttribute('aria-expanded', 'false');
    group.after(wrapper);
    submit.after(toggle);

    toggle.addEventListener('click', () => {
        if (pending(form)) return;
        const backup = input.disabled;
        delete form.dataset.otpSubmitted;
        fields.forEach(field => {
            field.value = '';
            field.disabled = backup;
            field.required = !backup;
            field.classList.toggle('input-necessarily', !backup);
        });
        group.hidden = backup;
        // The existing OTP layout may set display:flex, overriding [hidden].
        if (backup) group.style.setProperty('display', 'none');
        else group.style.removeProperty('display');
        wrapper.hidden = !backup;
        cells.forEach(cell => {
            cell.value = '';
            cell.disabled = !backup;
            cell.required = backup;
        });
        input.disabled = !backup;
        input.required = backup;
        input.classList.toggle('input-necessarily', backup);
        toggleLabel.textContent = translate(backup ? 'Use an authenticator code' : 'Use a backup code');
        toggle.setAttribute('aria-expanded', String(backup));
        sync(form);
        focus(backup ? cells[0] : fields[0]);
    });
};

export const totpBackupInput = () => {
    const scan = root => {
        if (root.matches?.(FORM)) enhance(root);
        root.querySelectorAll?.(FORM).forEach(enhance);
    };
    scan(document);
    new MutationObserver(records => {
        records.forEach(record => record.addedNodes.forEach(scan));
    }).observe(document.body, {childList: true, subtree: true});

    document.addEventListener('input', event => {
        const cell = event.target.closest?.(CELL);
        if (!cell || cell.disabled) return;
        const form = cell.closest(FORM);
        if (!form?.querySelector(INPUT)) return;
        const text = cell.value;
        cell.value = '';
        if (!text || !distribute(form, Number(cell.dataset.totpBackupPart), text)) sync(form);
    });
    document.addEventListener('paste', event => {
        const cell = event.target.closest?.(CELL);
        if (!cell || cell.disabled) return;
        const form = cell.closest(FORM);
        if (!form?.querySelector(INPUT)) return;
        event.preventDefault();
        distribute(form, Number(cell.dataset.totpBackupPart), event.clipboardData?.getData('text') || '');
    });
    document.addEventListener('focusin', event => {
        const cell = event.target.closest?.(CELL);
        if (cell && !cell.disabled) cell.select();
    });
    document.addEventListener('keydown', event => {
        const cell = event.target.closest?.(CELL);
        if (!cell || cell.disabled) return;
        const form = cell.closest(FORM);
        if (!form?.querySelector(INPUT)) return;
        const cells = cellsOf(form);
        const index = cells.indexOf(cell);
        if (event.key === 'Backspace') {
            event.preventDefault();
            const target = cell.value ? cell : cells[Math.max(0, index - 1)];
            target.value = '';
            sync(form);
            focus(target);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            focus(cells[Math.max(0, Math.min(7, index + (event.key === 'ArrowLeft' ? -1 : 1)))]);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            sync(form);
            const submit = form.querySelector(SUBMIT);
            if (!pending(form) && submit && !submit.disabled) submit.click();
        }
    });
    document.addEventListener('js-form:response', event => {
        const nodes = event.detail?.responseNodes || [event.detail?.responseNode];
        // Retire only the submitted list, and only once its next step arrives.
        if (event.target.querySelector?.(SUBMIT)?.name === 'totp_backup_codes_accept' &&
            nodes.some(node => node?.querySelector('[name="totp_backup_code_check"]'))) {
            event.target.remove();
            return;
        }
        if (!event.target.querySelector?.(INPUT)) return;
        if (event.target.querySelector(SUBMIT)?.name === 'totp_backup_code_check' &&
            nodes.some(node => node?.matches('[data-totp-done="1"]') || node?.querySelector('[data-totp-done="1"]'))) {
            event.target.remove();
            return;
        }
        cellsOf(event.target).forEach(cell => {cell.value = '';});
        sync(event.target);
    });
};
